import { useMemo, useState } from "react";
import { MEMBERS_LIST_TO_FETCH_PAGE_SIZE } from "../RoomConstants";
import { MemberListFilterData, MemberListFilters, MemberListPagination } from "../RoomMemberTypes";
import {
    getAllRoomMembersRef,
    getBannedRoomMembersRef,
    getInteractionBlockedMembersRef,
    getMutedRoomMembersRef,
} from "../RoomMembersService";
import useMembersListener from "../useMembersListener";

/**
 * Returns members list, pagination size, and increase page function for each
 * filter list: All members, muted, and banned
 * @param selectedFilter the current list displayed
 * @param roomId
 * @param isHost
 * @return {[memberList: MemberListFilterData, memberListPagination: MemberListPagination]} List of members for each filter,
 * and pagination size + function to increase page size
 */
export function useMembersModalLists(
    selectedFilter: MemberListFilters,
    roomId: string,
    isHost: boolean | undefined,
): [memberList: MemberListFilterData, memberListPagination: MemberListPagination] {
    // Initialize listeners for each list
    const [numPagesAllUsers, setNumPagesAllUsers] = useState(1);
    const allUsersRef = useMemo(
        () => (roomId ? getAllRoomMembersRef(roomId, numPagesAllUsers * MEMBERS_LIST_TO_FETCH_PAGE_SIZE) : undefined),
        [roomId, isHost, numPagesAllUsers],
    );
    const allUsers = useMembersListener(allUsersRef);

    const [numPagesMutedUsers, setNumPagesMutedUsers] = useState(1);
    const mutedByHostRef = useMemo(
        () =>
            roomId && isHost
                ? getMutedRoomMembersRef(roomId, numPagesMutedUsers * MEMBERS_LIST_TO_FETCH_PAGE_SIZE)
                : undefined,
        [roomId, isHost, numPagesMutedUsers],
    );
    const mutedByHostUsers = useMembersListener(mutedByHostRef);

    const mutedByReportsRef = useMemo(
        () =>
            roomId && isHost
                ? getInteractionBlockedMembersRef(roomId, numPagesMutedUsers * MEMBERS_LIST_TO_FETCH_PAGE_SIZE)
                : undefined,
        [roomId, isHost, numPagesMutedUsers],
    );
    const mutedByReportsUsers = useMembersListener(mutedByReportsRef);

    const [numPagesBannedUsers, setNumPagesBannedUsers] = useState(1);
    const bannedRoomMembersRef = useMemo(
        () =>
            roomId && isHost
                ? getBannedRoomMembersRef(roomId, numPagesBannedUsers * MEMBERS_LIST_TO_FETCH_PAGE_SIZE)
                : undefined,
        [roomId, isHost, numPagesBannedUsers],
    );
    const bannedUsers = useMembersListener(bannedRoomMembersRef);

    // Construct return objects for lists and pagination size
    const membersList: MemberListFilterData = useMemo(
        () => ({
            allMembers: {
                list: allUsers,
            },
            muted: {
                list: isHost ? [...new Set([...mutedByHostUsers, ...mutedByReportsUsers])] : undefined,
            },
            banned: {
                list: isHost ? bannedUsers : undefined,
            },
        }),
        [allUsers, mutedByHostUsers, mutedByReportsUsers, bannedUsers],
    );

    const memberListPagination: MemberListPagination = useMemo(
        () => ({
            allMembers: {
                numPages: numPagesAllUsers,
                increasePageSize: () => setNumPagesAllUsers(numPagesAllUsers + 1),
            },
            ...(isHost && {
                muted: {
                    numPages: numPagesMutedUsers,
                    increasePageSize: () => setNumPagesMutedUsers(numPagesMutedUsers + 1),
                },
            }),
            ...(isHost && {
                banned: {
                    numPages: numPagesBannedUsers,
                    increasePageSize: () => setNumPagesBannedUsers(numPagesBannedUsers + 1),
                },
            }),
        }),
        [numPagesAllUsers, numPagesMutedUsers, numPagesBannedUsers],
    );

    return [membersList, memberListPagination];
}
